import {v2} from "oc-threejs/short3";

const _v = v2();

const epsilon = 0.000000000001;

/**
 * Eine Gerade in 2D
 * Vor allem für Schnitttests benötigt
 */
export default class Line2d {

    origin = v2(0, 0);
    direction = v2(1, 0);
    normal = v2(0, 1);
    constant = 0;

    // optional, ein zweiter Punkt neben dem Origin, für Streckenabschnitte interessant
    point2 = null;

    constructor(origin, direction) {
        if (origin) {
            this.origin.copy(origin);
        }
        if (direction) {
            this.direction.copy(direction).normalize();
        }
        this.updateNormalAndConstant();
    }

    setFromPoints(point1, point2) {
        this.point2 = point2.clone();
        this.origin.copy(point1);
        this.direction.copy(point2).sub(point1).normalize();
        this.updateNormalAndConstant();
        return this;
    }

    /**
     * Transformiert einen Richtungs-Vector (nicht Orts-Vector!) in das "eigene" Koordinatensystem,
     * wobei der X-Anteil die eigene direction und der Y-Anteil die eigene normale ist.
     */
    directionInOwnBasis(v) {
        const x = this.direction.dot(v);
        const y = this.normal.dot(v);
        return v2(x, y);
    }

    /**
     * an welchen Punkten sind zwei Linienabstände genau gleich distance?
     * @param line
     * @param distance
     */
    pointsWhereDistanceToLineIs(line, distance) {
        const intersection = this.intersectLine(line);
        if (!intersection) {
            return null;
        }
        const d = this.directionInOwnBasis(line.direction);
        const dx = distance * d.x / d.y;
        const points = [
            this.direction.clone().multiplyScalar(-dx).add(intersection),
            this.direction.clone().multiplyScalar(dx).add(intersection)];
    }

    /**
     * wandelt einen Ortsvektor in einen lokalen Basisvektor bezogen auf den origin und direction/normal um
     */
    worldToLocal(v) {
        const d = v.clone().sub(this.origin);
        return this.directionInOwnBasis(d);
    }

    /**
     * wandelt einen lokalen Basisvektor bezogen auf den origin und direction/normal in einem World-Ortsvektor um
     */
    localToWorld(v) {
        const d = this.direction.clone().multiplyScalar(v.x);
        const n = this.normal.clone().multiplyScalar(v.y);
        return d.add(n).add(this.origin);
    }

    /**
     * gibt den Schnittpunkt mit einer anderen Linie zurück, oder null
     * @param {Line2d} line
     * @param {*} [optionalTarget=undefined]
     */
    intersectLine(line, optionalTarget = undefined) {
        const denominator = line.normal.dot(this.direction);
        if (Math.abs(denominator) < epsilon) {
            // line is parallel or identical
            return null;
        }
        const t = -(this.origin.dot(line.normal) + line.constant) / denominator;
        return this.at(t, optionalTarget);
    }

    /**
     * gibt die y-Koordinate zurück, an der die Line die Parallele zur X-Achse schneidet, oder POSITIVE_INFINITY
     * @param {number} x
     * @returns {number}
     */
    yOfX(x) {
        if (Math.abs(this.direction.x) < epsilon) {
            return Number.POSITIVE_INFINITY;
        }
        const dx = x - this.origin.x;
        const dy = dx * this.direction.y / this.direction.x;
        return this.origin.y + dy;
    }

    /**
     * gibt die x-Koordinate zurück, an der die Line die Parallele zur Y-Achse schneidet, oder POSITIVE_INFINITY
     * @param {number} y
     * @returns {number}
     */
    xOfY(y) {
        if (Math.abs(this.direction.y) < epsilon) {
            return Number.POSITIVE_INFINITY;
        }
        const dy = y - this.origin.y;
        const dx = dy * this.direction.x / this.direction.y;
        return this.origin.x + dx;
    }

    /**
     * gibt den Punkt auf der Linie mit Distanz t zum Origin zurück
     */
    at(t, optionalTarget = v2()) {
        return optionalTarget.copy(this.direction).multiplyScalar(t).add(this.origin);
    }

    distanceToPoint(point) {
        return Math.sqrt(this.distanceSqToPoint(point));
    }

    projectPoint(point) {
        const v = v2();
        const directionDistance = v.subVectors(point, this.origin).dot(this.direction);
        v.copy(this.direction).multiplyScalar(directionDistance).add(this.origin);
        return v;
    }

    distanceSqToPoint(point) {
        return this.projectPoint(point).distanceToSquared(point);
    }

    copy(line) {
        this.origin.copy(line.origin);
        this.direction.copy(line.direction);
        this.normal.copy(line.normal);
        this.constant = line.constant;
    }

    clone() {
        return new Line2d().copy(this);
    }

    setParallelTo(line, distance = 0) {
        this.copy(line);
        this.origin.copy(line.normal).multiplyScalar(distance).add(line.origin);
        this.updateNormalAndConstant();
    }

    setOrthogonalTo(line, distance = 0) {
        this.copy(line);
        this.origin.copy(line.direction).multiplyScalar(distance).add(line.origin);
        this.direction.copy(line.normal);
        this.updateNormalAndConstant();
    }

    updateNormalAndConstant() {
        this.normal.set(-this.direction.y, this.direction.x);
        this.constant = -this.origin.dot(this.normal);
    }

}

